import React, { useEffect, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { FormInputs } from "../../../../../../types/walletTypes";
import { savedAsiaPayoutData } from "../../../../redux/formDataSlice";
import {
  ContentWrapper,
  InputWrapper,
  GenericTitle,
  GenericSubTitle,
  ItemWrapper,
  RWrapper,
  Error,
  ButtonsWrapper,
  BalanceExceptionWrapper,
} from "../../../shared/styles";
import CustomRadio from "../../../../../../components/common/RadioInput";
import { Input } from "../../../../../../components/common/Input";
import { Button } from "../../../../../../components/common/Button";
import Loader from "../../../../../../components/common/Loader";
import { ReactComponent as NotifyIcon } from "../../../../../../assets/icons/notify-icon.svg";
import { CHINA_TRANSACTION_LIMITS } from "../../../shared/const";

interface ChinaSenderFormProps {
  handleNext?: () => void;
  handlePrevious?: () => void;
}

const ChinaSenderForm: React.FC<ChinaSenderFormProps> = ({
  handleNext,
  handlePrevious,
}) => {
  const dispatch = useAppDispatch();
  const asiaPayoutData = useAppSelector((state) => (state.formData as any)?.asiaPayoutData?.cn || {});
  const { loading } = useAppSelector((state) => state.wallets || {});

  const [paymentClassification, setPaymentClassification] = useState<"Service" | "Goods">(
    asiaPayoutData?.paymentClassification ||
    (asiaPayoutData?.purpose === "Payments for goods" ? "Goods" : "Service")
  );

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm<FormInputs>({
    defaultValues: asiaPayoutData,
  });

  useEffect(() => {
    if (asiaPayoutData && Object.keys(asiaPayoutData).length > 0) {
      reset({ ...asiaPayoutData });
      if (asiaPayoutData.paymentClassification) {
        setPaymentClassification(asiaPayoutData.paymentClassification as any);
      }
    }
  }, [asiaPayoutData, reset]);

  const updatePaymentClassification = (value: "Service" | "Goods") => {
    setPaymentClassification(value);
    setValue("paymentClassification", value);

    const newPurpose = value === "Goods" ? "Payments for goods" : "Payment for services";
    setValue("purpose", newPurpose);

    const updatedData: FormInputs = {
      ...asiaPayoutData,
      paymentClassification: value,
      purpose: newPurpose,
    };
    dispatch(savedAsiaPayoutData({ country: "cn", data: updatedData }));
  };

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    const updatedData: FormInputs = {
      ...asiaPayoutData,
      ...data,
      paymentClassification,
      purpose: paymentClassification === "Goods" ? "Payments for goods" : "Payment for services",
    };
    dispatch(savedAsiaPayoutData({ country: "cn", data: updatedData }));
    handleNext?.();
  };

  const enteredAmount = Number(asiaPayoutData?.amount || 0);

  const selectedLimit =
    paymentClassification === "Goods"
      ? CHINA_TRANSACTION_LIMITS.B2B_GOODS
      : CHINA_TRANSACTION_LIMITS.B2B_SERVICES;

  const selectedCategoryLabel =
    paymentClassification === "Goods" ? "physical goods" : "services";

  const isAmountOverLimit = enteredAmount > selectedLimit;

  const disableButton = !isValid || isAmountOverLimit;

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      <InputWrapper>
        <GenericTitle>Select what your payment is for.</GenericTitle>
        <GenericSubTitle>Choose the option that best describes your payment.</GenericSubTitle>
        <ItemWrapper>
          <RWrapper>
            <CustomRadio
              label="Goods"
              value="Goods"
              onClick={() => updatePaymentClassification("Goods")}
              selected={paymentClassification === "Goods"}
            />
          </RWrapper>
          <RWrapper>
            <CustomRadio
              label="Service"
              value="Service"
              onClick={() => updatePaymentClassification("Service")}
              selected={paymentClassification === "Service"}
            />
          </RWrapper>
        </ItemWrapper>
        {isAmountOverLimit && (
          <>
            <BalanceExceptionWrapper >
              <NotifyIcon />
              <span>
                The amount entered ({enteredAmount.toLocaleString()} CNY) exceeds the
                limit of {selectedLimit.toLocaleString()} CNY for {selectedCategoryLabel}.
              </span>
            </BalanceExceptionWrapper>
            <BalanceExceptionWrapper>
              <a
                href="#back"
                onClick={(e) => {
                  e.preventDefault();
                  handlePrevious?.();
                }}
                style={{ cursor: "pointer" }}
              >
                Go back to update amount
              </a>
            </BalanceExceptionWrapper>
          </>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="senderFullName"
          control={control}
          rules={{ required: "Full name is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Full name"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors?.senderFullName}
            />
          )}
        />
        {errors?.senderFullName?.message && (
          <Error>{String(errors.senderFullName.message)}</Error>
        )}
      </InputWrapper>

      {paymentClassification === "Goods" &&
        <>
          <InputWrapper>
            <Controller
              name="logisticsCompany"
              control={control}
              rules={{ required: "Logistics provider is required" }}
              render={({ field }) => (
                <Input
                  type="text"
                  label="Logistics provider"
                  value={field.value || ""}
                  onChange={field.onChange}
                  hasError={!!errors?.logisticsCompany}
                />
              )}
            />
            {errors?.logisticsCompany?.message && (
              <Error>{String(errors.logisticsCompany.message)}</Error>
            )}
          </InputWrapper>

          <InputWrapper>
            <Controller
              name="logisticsProviderCode"
              control={control}
              rules={{ required: "Logistics provider code is required" }}
              render={({ field }) => (
                <Input
                  type="text"
                  label="Logistics provider code"
                  value={field.value || ""}
                  onChange={field.onChange}
                  hasError={!!errors?.logisticsProviderCode}
                />
              )}
            />
            {errors?.logisticsProviderCode?.message && (
              <Error>{String(errors.logisticsProviderCode.message)}</Error>
            )}
          </InputWrapper>

          <InputWrapper>
            <Controller
              name="trackingNumber"
              control={control}
              rules={{ required: "Tracking number is required" }}
              render={({ field }) => (
                <Input
                  type="text"
                  label="Tracking number"
                  value={field.value || ""}
                  onChange={field.onChange}
                  hasError={!!errors?.trackingNumber}
                />
              )}
            />
            {errors?.trackingNumber?.message && (
              <Error>{String(errors.trackingNumber.message)}</Error>
            )}
          </InputWrapper>

          <ItemWrapper>
            <InputWrapper>
              <Controller
                name="itemName"
                control={control}
                rules={{ required: "Item name is required" }}
                render={({ field }) => (
                  <Input
                    type="text"
                    label="Item name"
                    value={field.value || ""}
                    onChange={field.onChange}
                    hasError={!!errors?.itemName}
                  />
                )}
              />
              {errors?.itemName?.message && (
                <Error>{String(errors.itemName.message)}</Error>
              )}
            </InputWrapper>

            <InputWrapper>
              <Controller
                name="itemQuantity"
                control={control}
                rules={{ required: "Item quantity is required" }}
                render={({ field }) => (
                  <Input
                    type="number"
                    label="Item quantity"
                    value={field.value !== undefined ? String(field.value) : ""}
                    onChange={field.onChange}
                    hasError={!!errors?.itemQuantity}
                  />
                )}
              />
              {errors?.itemQuantity?.message && (
                <Error>{String(errors.itemQuantity.message)}</Error>
              )}
            </InputWrapper>
          </ItemWrapper>

          <InputWrapper>
            <Controller
              name="itemAmount"
              control={control}
              rules={{ required: "Item amount is required" }}
              render={({ field }) => (
                <Input
                  type="number"
                  label="Item amount"
                  value={field.value !== undefined ? String(field.value) : ""}
                  onChange={field.onChange}
                  hasError={!!errors?.itemAmount}
                />
              )}
            />
            {errors?.itemAmount?.message && (
              <Error>{String(errors.itemAmount.message)}</Error>
            )}
          </InputWrapper>

          <InputWrapper>
            <Controller
              name="itemDescription"
              control={control}
              rules={{ required: "Description of item is required" }}
              render={({ field }) => (
                <Input
                  type="text"
                  label="Description of item"
                  value={field.value || ""}
                  onChange={field.onChange}
                  hasError={!!errors?.itemDescription}
                />
              )}
            />
            {errors?.itemDescription?.message && (
              <Error>{String(errors.itemDescription.message)}</Error>
            )}
          </InputWrapper>
        </>
      }

      <ButtonsWrapper>
        <Button onClick={handlePrevious} theme="alternate" height="48px" width="203px">
          Back
        </Button>
        <Button onClick={handleSubmit(onSubmit)} disabled={disableButton} label="Continue" height="48px" />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default ChinaSenderForm;
